import React from 'react';
import { Link } from 'react-router-dom';
import { useGame } from '@/context/GameContext';
import { MaterialIcon } from '@/components/ui/MaterialIcon';
import { PHASES, getRecommendedNext, hasAnyProgress, isPhaseUnlocked } from '@/data/phases';
import SEO from '@/components/layout/SEO';

function sumUnlockedCourses() {
    return PHASES.reduce(
        (count, phase) => count + phase.courses.filter((course) => course.isAvailable).length,
        0,
    );
}

export default function CoursesPage() {
    const { stats, progress, rank } = useGame();

    const recommended = getRecommendedNext(progress);
    const userHasProgress = hasAnyProgress(progress);
    const level = stats?.level ?? 1;
    const xp = stats?.xp ?? 0;
    const streakDays = stats?.streak_days ?? 0;
    const completedModules = Object.values(progress).filter((status) => status === 'completed').length;
    const unlockedCourses = sumUnlockedCourses();

    return (
        <div className="relative overflow-hidden bg-[#0e0e0e] text-white">
            <SEO title="STARTZONE.DEV - Skill Tree" />

            <div className="pointer-events-none absolute inset-0 opacity-[0.18]" style={{ background: 'linear-gradient(to bottom, transparent 50%, rgba(140, 172, 255, 0.05) 50%)', backgroundSize: '100% 4px' }} />
            <div className="pointer-events-none absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'radial-gradient(#8cacff 0.5px, transparent 0.5px)', backgroundSize: '24px 24px' }} />
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.05]">
                <span className="font-['Space_Grotesk'] text-[16rem] font-black italic tracking-[-0.08em] md:text-[28rem]">SZ</span>
            </div>
            <div className="pointer-events-none absolute left-1/2 top-[32%] h-[30rem] w-[30rem] -translate-x-1/2 rounded-full bg-[#8cacff]/5 blur-[120px]" />

            <section className="relative mx-auto max-w-[96rem] px-4 py-6 md:px-6 md:pt-6">
                <div className="grid grid-cols-1 gap-6 xl:grid-cols-[16rem_minmax(0,1fr)]">
                    <aside className="space-y-6 bg-[#0e0e0e] xl:min-h-[calc(100vh-9rem)] xl:border-r xl:border-[#484848]/15 xl:pr-6">
                        <div className="border-l-2 border-[#8cacff] pl-4 pt-2">
                            <span className="block font-['Space_Grotesk'] text-[10px] font-black uppercase tracking-[0.18em] text-[#8cacff]">
                                STATUS: ONLINE
                            </span>
                            <span className="mt-1 block font-['Space_Grotesk'] text-2xl font-black uppercase tracking-tight text-white">
                                LEVEL {level}
                            </span>
                            <span className="mt-1 block font-mono text-[11px] uppercase text-[#8cacff]/60">
                                ELITE DEPLOYER
                            </span>
                        </div>

                        <Link
                            to="/profile"
                            className="block w-full bg-[#1f2020] px-4 py-3 text-center font-['Space_Grotesk'] text-xs font-black uppercase tracking-[0.24em] text-[#8cacff] transition-colors hover:bg-[#252626]"
                        >
                            REDEEM XP
                        </Link>

                        <nav className="space-y-1">
                            {PHASES.map((phase, index) => {
                                const active = index === 0;
                                const unlockedCount = phase.courses.filter((course) => course.isAvailable).length;
                                return (
                                    <Link
                                        key={phase.number}
                                        to={recommended ? `/courses/${recommended.id}` : '/courses'}
                                        className={`flex items-center gap-3 py-3 pl-4 font-['Space_Grotesk'] text-sm font-bold uppercase tracking-tight transition-colors ${
                                            active
                                                ? 'border-l-4 border-[#00cffc] bg-[#131313] text-[#00cffc]'
                                                : 'text-[#484848] hover:bg-[#131313] hover:text-[#8cacff]'
                                        }`}
                                    >
                                        <MaterialIcon icon={active ? 'terminal' : unlockedCount > 0 ? 'explore' : 'lock'} size={18} className="text-current" />
                                        <span className="truncate">{phase.name}</span>
                                    </Link>
                                );
                            })}
                        </nav>
                    </aside>

                    <div className="space-y-6">
                        <header className="flex flex-col justify-between gap-8 border-l-4 border-[#8cacff] bg-[#131313] px-4 py-5 md:flex-row md:items-end md:px-6">
                            <div>
                                <div className="mb-2 flex items-center gap-2">
                                    <span className="font-mono text-xs font-bold uppercase tracking-[0.18em] text-[#00cffc]">
                                        [ COURSE_ID: GRID_01 ]
                                    </span>
                                    <span className="h-px w-8 bg-[#484848]/30" />
                                    <span className="font-mono text-xs uppercase text-[#acabaa]">
                                        {userHasProgress ? 'Continue deployment' : 'Begin the grid'}
                                    </span>
                                </div>
                                <h1 className="font-['Space_Grotesk'] text-4xl font-black uppercase tracking-tighter text-white md:text-6xl">
                                    <span className="text-[#8cacff] italic">Question</span> Map
                                </h1>
                                <div className="mt-5 flex flex-wrap items-center gap-3 text-[10px] font-mono uppercase">
                                    <div className="bg-[#1f2020] px-3 py-1 text-[#acabaa]">
                                        UNLOCKED: <span className="text-[#8cacff] font-bold">{unlockedCourses}</span>
                                    </div>
                                    <div className="bg-[#1f2020] px-3 py-1 text-[#acabaa]">
                                        COMPLETED: <span className="text-[#00cffc] font-bold">{completedModules}</span> MODULES
                                    </div>
                                    <div className="bg-[#1f2020] px-3 py-1 text-[#acabaa]">
                                        XP: <span className="text-[#d674ff] font-bold">{xp.toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col items-start gap-2 md:items-end">
                                <span className="font-mono text-[10px] uppercase tracking-widest text-[#acabaa]">
                                    Global Completion Rank
                                </span>
                                <div className="text-3xl font-['Space_Grotesk'] font-black italic text-white">
                                    {rank !== null ? `TOP ${Math.min(99, rank)}%` : 'UNRANKED'}
                                </div>
                            </div>
                        </header>

                        {recommended ? (
                            <Link
                                to={`/courses/${recommended.id}`}
                                className="flex flex-col gap-4 border border-[#484848]/15 bg-[#131313] px-4 py-4 transition-colors hover:bg-[#1f2020] md:flex-row md:items-center md:px-6"
                            >
                                <span className="text-3xl">{recommended.icon}</span>
                                <div className="min-w-0 flex-1">
                                    <p className="mb-1 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#8cacff]">
                                        {userHasProgress ? 'Continue where you left off' : 'Start here'}
                                    </p>
                                    <p className="truncate font-['Space_Grotesk'] text-sm font-black uppercase text-white">
                                        {recommended.title}
                                    </p>
                                </div>
                                <span className="font-['Space_Grotesk'] text-xs font-black uppercase tracking-[0.18em] text-[#00cffc]">
                                    {userHasProgress ? 'Resume' : 'Begin'}
                                </span>
                            </Link>
                        ) : null}

                        <div className="relative min-h-[48rem] overflow-hidden bg-[#131313] p-4 md:p-8">
                            <div className="absolute inset-0 opacity-5 pointer-events-none flex items-center justify-center">
                                <span className="font-black italic text-[15rem] md:text-[24rem]">SZ</span>
                            </div>
                            <svg className="pointer-events-none absolute inset-0 h-full w-full opacity-20" preserveAspectRatio="none">
                                <path d="M 50 120 L 50 260" fill="none" stroke="#8cacff" strokeWidth="2" />
                                <path d="M 50 260 L 240 360" fill="none" stroke="#8cacff" strokeWidth="2" />
                                <path d="M 50 260 L 440 360" fill="none" stroke="#8cacff" strokeWidth="2" />
                                <path d="M 240 520 L 240 640" fill="none" stroke="#484848" strokeWidth="2" />
                                <path d="M 440 520 L 440 640" fill="none" stroke="#00cffc" strokeDasharray="4" strokeWidth="2" />
                                <path d="M 240 760 L 340 840" fill="none" stroke="#484848" strokeWidth="2" />
                                <path d="M 440 760 L 340 840" fill="none" stroke="#484848" strokeWidth="2" />
                            </svg>

                            <div className="relative mx-auto flex max-w-5xl flex-col items-center gap-12 py-6">
                                <div className="group relative w-24">
                                    <div className="mx-auto flex h-20 w-20 rotate-45 items-center justify-center border-2 border-[#8cacff] bg-[#1f2020] transition-transform group-hover:scale-110">
                                        <MaterialIcon icon="dns" size={28} fill className="text-[#8cacff] -rotate-45" />
                                    </div>
                                    <div className="absolute -bottom-16 left-1/2 w-40 -translate-x-1/2 text-center">
                                        <p className="font-mono text-[10px] font-black uppercase text-[#8cacff]">
                                            Core Foundations
                                        </p>
                                        <p className="font-mono text-[9px] uppercase text-[#acabaa]">
                                            COMPLETE // +500XP
                                        </p>
                                    </div>
                                </div>

                                <div className="flex w-full justify-between gap-6 px-2 md:px-10">
                                    {PHASES[0].courses.map((course) => {
                                        const unlocked = isPhaseUnlocked(0, progress) || course.id === 'terminal';
                                        return (
                                            <Link key={course.id} to={`/courses/${course.id}`} className="group relative">
                                                <div className={`mx-auto flex h-20 w-20 rotate-45 items-center justify-center border-2 transition-transform group-hover:scale-110 ${unlocked ? 'border-[#8cacff] bg-[#1f2020] skill-node-completed' : 'border-[#484848] bg-[#191a1a] opacity-40 grayscale'}`}>
                                                    <span className={`material-symbols-outlined -rotate-45 text-3xl ${unlocked ? 'text-[#8cacff]' : 'text-[#484848]'}`} style={{ fontVariationSettings: "'FILL' 1" }}>
                                                        {course.id === 'terminal' ? 'terminal' : 'restart_alt'}
                                                    </span>
                                                </div>
                                                <div className="absolute -bottom-16 left-1/2 w-48 -translate-x-1/2 text-center">
                                                    <p className={`font-mono text-[10px] font-black uppercase ${unlocked ? 'text-[#8cacff]' : 'text-[#484848]'}`}>
                                                        {course.title}
                                                    </p>
                                                    <p className="font-mono text-[9px] uppercase text-[#acabaa]">
                                                        {unlocked ? 'AVAILABLE' : 'LOCKED'}
                                                    </p>
                                                </div>
                                            </Link>
                                        );
                                    })}
                                </div>

                                <div className="flex w-full flex-wrap justify-between gap-8 px-2 md:px-10">
                                    {PHASES[1].courses.map((course) => {
                                        const unlocked = isPhaseUnlocked(1, progress);
                                        return (
                                            <Link key={course.id} to={`/courses/${course.id}`} className="group relative">
                                                <div className={`mx-auto flex h-20 w-20 rotate-45 items-center justify-center border-2 transition-transform group-hover:scale-110 ${unlocked ? 'border-[#00cffc] bg-[#2c2c2c] skill-node-active' : 'border-[#484848] bg-[#191a1a] opacity-40 grayscale'}`}>
                                                    <span className={`material-symbols-outlined -rotate-45 text-3xl ${unlocked ? 'text-[#00cffc]' : 'text-[#484848]'}`} style={{ fontVariationSettings: "'FILL' 1" }}>
                                                        {course.id === 'http' ? 'lan' : course.id === 'dns' ? 'language' : 'web'}
                                                    </span>
                                                </div>
                                                <div className="absolute -bottom-16 left-1/2 w-48 -translate-x-1/2 text-center">
                                                    <p className={`font-mono text-[10px] font-black uppercase ${unlocked ? 'text-[#00cffc]' : 'text-[#484848]'}`}>
                                                        {course.title}
                                                    </p>
                                                    <p className={`font-mono text-[9px] uppercase ${unlocked ? 'text-[#00cffc]/70' : 'text-[#484848]'}`}>
                                                        {unlocked ? 'ACTIVE' : 'LOCKED'}
                                                    </p>
                                                </div>
                                            </Link>
                                        );
                                    })}
                                </div>

                                <div className="flex w-full flex-wrap justify-between gap-8 px-2 md:px-10">
                                    {PHASES.slice(2).map((phase) => (
                                        <div key={phase.number} className="group relative opacity-40">
                                            <div className="mx-auto flex h-20 w-20 rotate-45 items-center justify-center border-2 border-[#484848] bg-[#191a1a] grayscale">
                                                <MaterialIcon icon="lock" size={28} className="text-[#484848] -rotate-45" />
                                            </div>
                                            <div className="absolute -bottom-16 left-1/2 w-48 -translate-x-1/2 text-center">
                                                <p className="font-mono text-[10px] font-black uppercase text-[#484848]">
                                                    {phase.name}
                                                </p>
                                                <p className="font-mono text-[9px] uppercase text-[#484848]">
                                                    LOCKED // REQ: PRIOR PHASE
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
